import { useEffect, useState, type RefObject } from 'react';
import { isEmpty, round } from 'lodash/fp';
import type { Dimensions } from 'app/lib/types/ui';

type DimensionKey = keyof Dimensions;

const lookup =
    (entries: ResizeObserverEntry[]) =>
    (attr: DimensionKey): number =>
        round(entries[0]?.contentRect[attr] ?? 0);

export function useResizeObserver(ref: RefObject<HTMLElement | null>): Dimensions {
    const [attrs, setAttrs] = useState<Dimensions>({
        width: 0,
        height: 0
    });

    useEffect(() => {
        const element = ref.current;

        if (element) {
            const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                if (isEmpty(entries)) {
                    return;
                }

                const current = lookup(entries);

                setAttrs((previous: Dimensions) => {
                    const width = current('width');
                    const height = current('height');

                    if (previous.width === width && previous.height === height) {
                        return previous;
                    }

                    return { width, height };
                });
            });

            observer.observe(element);

            return () => observer.unobserve(element);
        }
    }, [ref]);

    return attrs;
}
